/**
 * Single source of truth for every enumerated value in the system.
 * Never write these strings inline — import the named const, the array, or the type.
 */

export const EVENT_TYPES = ['product', 'fee', 'membership'] as const
export type EventType = (typeof EVENT_TYPES)[number]

export const EVENT_DRAFT = 'draft'
export const EVENT_OPEN = 'open'
export const EVENT_CLOSED = 'closed'
export const EVENT_STATUSES = [EVENT_DRAFT, EVENT_OPEN, EVENT_CLOSED] as const
export type EventStatus = (typeof EVENT_STATUSES)[number]

export const ORDER_PENDING = 'pending'
export const ORDER_PAID = 'paid'
export const ORDER_SHIPPED = 'shipped'
/** Terminal, and set by hand: the organiser knows it arrived, the system cannot. */
export const ORDER_RECEIVED = 'received'
/** Called off by the organiser. Nothing gets made, whether or not money moved. */
export const ORDER_CANCELLED = 'cancelled'
export const ORDER_EXPIRED = 'expired'
export const ORDER_STATUSES = [
  ORDER_PENDING,
  ORDER_PAID,
  ORDER_SHIPPED,
  ORDER_RECEIVED,
  ORDER_CANCELLED,
  ORDER_EXPIRED,
] as const
export type OrderStatus = (typeof ORDER_STATUSES)[number]

/**
 * Statuses that count as money received.
 *
 * Every one of these is a shirt the factory has to make and a row in the export.
 * Unlike the label and tone maps, this is an array — so adding a status and
 * forgetting this line compiles cleanly and quietly drops those orders from the
 * production count. A test in pricing.test.ts asserts the post-payment statuses
 * are all here.
 */
export const SETTLED_STATUSES = [ORDER_PAID, ORDER_SHIPPED, ORDER_RECEIVED] as const

export const DELIVERY_SHIP = 'ship'
export const DELIVERY_PICKUP = 'pickup'
export const DELIVERY_TYPES = [DELIVERY_SHIP, DELIVERY_PICKUP] as const
export type DeliveryType = (typeof DELIVERY_TYPES)[number]

export const PAYMENT_MODE_QR = 'qr'
export const PAYMENT_MODE_REDIRECT = 'redirect'
export const PAYMENT_MODES = [PAYMENT_MODE_QR, PAYMENT_MODE_REDIRECT] as const
export type PaymentMode = (typeof PAYMENT_MODES)[number]

export const HISTORY_ACTIONS = [
  'created',
  'paid',
  'size_changed',
  'marked_shipped',
  'marked_received',
  'cancelled',
  'details_edited',
  'expired',
  'lookup_sent',
] as const
export type HistoryAction = (typeof HISTORY_ACTIONS)[number]

/** Non-human actors written to order history. Anything else is an admin e-mail. */
export const ACTOR_SYSTEM = 'system'
export const ACTOR_PAYMENT = 'payment'
/** The buyer acting on their own order from /result — not an admin, not the system. */
export const ACTOR_BUYER = 'buyer'
export const SYSTEM_ACTORS = [ACTOR_SYSTEM, ACTOR_PAYMENT, ACTOR_BUYER] as const

/** Firestore paths — referenced by rules, functions, web and scripts. */
export const COLLECTIONS = {
  config: 'config',
  participants: 'participants',
  orders: 'orders',
  history: 'history',
  processedWebhooks: 'processedWebhooks',
  rateLimits: 'rateLimits',
  auditLog: 'auditLog',
  /*
   * Who may sign in to /admin. A TOP-LEVEL collection, deliberately not a field on
   * `config/event`: that document is world-readable, so an admin list living there
   * would publish the organisers' addresses to every visitor. Firestore rules deny
   * this collection to clients entirely — it is reached only through callables.
   */
  adminAccess: 'adminAccess',
} as const

export const CONFIG_DOC_ID = 'event'
export const ADMIN_ACCESS_DOC_ID = 'list'
export const CONFIG_DOC_PATH = `${COLLECTIONS.config}/${CONFIG_DOC_ID}`

/** Order-id alphabet: no I/O/0/1 so a human can read it off a screen. */
export const ORDER_ID_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
export const ORDER_ID_GROUP_LEN = 4
export const ORDER_ID_GROUPS = 2

export const MAX_LINES_PER_ORDER = 20
export const MAX_QTY_PER_LINE = 20

/** Beam (and every processor worth using) counts money in the smallest unit. */
export const SATANG_PER_BAHT = 100
export const CURRENCY_THB = 'THB'

export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  [ORDER_PENDING]: 'รอชำระเงิน',
  [ORDER_PAID]: 'จ่ายแล้ว',
  [ORDER_SHIPPED]: 'จัดส่งแล้ว',
  [ORDER_RECEIVED]: 'รับแล้ว',
  [ORDER_CANCELLED]: 'ยกเลิกแล้ว',
  [ORDER_EXPIRED]: 'หมดอายุ',
}

export const DELIVERY_TYPE_LABEL: Record<DeliveryType, string> = {
  [DELIVERY_SHIP]: 'จัดส่งทางไปรษณีย์',
  [DELIVERY_PICKUP]: 'รับเองที่บริษัท',
}

/** Changes to the event itself, as opposed to an order. Written by the admin callables. */
export const AUDIT_ACTIONS = [
  'event_updated',
  'participants_imported',
  'participants_removed',
  'orders_exported',
  'admins_updated',
] as const
export type AuditAction = (typeof AUDIT_ACTIONS)[number]

export const AUDIT_ACTION_LABEL: Record<AuditAction, string> = {
  event_updated: 'แก้ไขการตั้งค่างาน',
  participants_imported: 'นำเข้ารายชื่อ',
  participants_removed: 'ลบรายชื่อ',
  orders_exported: 'ดาวน์โหลดข้อมูลออเดอร์',
  admins_updated: 'แก้ไขรายชื่อผู้ดูแล',
}

/** Where an uploaded product image lives, and where a deleted one goes. */
export const IMAGE_PREFIX = 'event-images'
export const TRASH_PREFIX = 'trash'
export const MAX_IMAGE_BYTES = 2 * 1024 * 1024

export const HISTORY_ACTION_LABEL: Record<HistoryAction, string> = {
  created: 'สร้างออเดอร์',
  paid: 'ชำระเงินสำเร็จ',
  size_changed: 'เปลี่ยน size',
  marked_shipped: 'ทำเครื่องหมายจัดส่งแล้ว',
  marked_received: 'ยืนยันว่าผู้สั่งได้รับแล้ว',
  cancelled: 'ยกเลิกออเดอร์',
  details_edited: 'แก้ไขข้อมูลออเดอร์',
  expired: 'หมดอายุอัตโนมัติ',
  lookup_sent: 'ส่งรายละเอียดทางอีเมล',
}

/**
 * Couriers the organiser can pick when entering a tracking number.
 *
 * `other` exists so a courier nobody listed never blocks sending the number — the
 * buyer still gets the digits, just without a link.
 */
export const SHIPPING_CARRIERS = [
  'thailand_post',
  'flash',
  'kerry',
  'jt',
  'ninja_van',
  'spx',
  'other',
] as const
export type ShippingCarrier = (typeof SHIPPING_CARRIERS)[number]

export const SHIPPING_CARRIER_LABEL: Record<ShippingCarrier, string> = {
  thailand_post: 'ไปรษณีย์ไทย',
  flash: 'Flash Express',
  kerry: 'Kerry Express',
  jt: 'J&T Express',
  ninja_van: 'Ninja Van',
  spx: 'SPX Express',
  other: 'อื่นๆ',
}

/**
 * Where "ติดตามพัสดุ" points, by courier.
 *
 * One map, because three places need the link and a tracking URL that differs
 * between the e-mail and the website is a support ticket. `null` means we have no
 * reliable deep link for that courier, and every caller must then show the number
 * as plain text rather than a link that 404s.
 */
const TRACKING_URL: Record<ShippingCarrier, ((code: string) => string) | null> = {
  thailand_post: (code) => `https://track.thailandpost.co.th/?trackNumber=${encodeURIComponent(code)}`,
  flash: (code) => `https://www.flashexpress.com/fle/tracking?se=${encodeURIComponent(code)}`,
  kerry: (code) => `https://th.kerryexpress.com/th/track/?track=${encodeURIComponent(code)}`,
  jt: (code) => `https://www.jtexpress.co.th/index/query/gzquery.html?bills=${encodeURIComponent(code)}`,
  ninja_van: (code) => `https://www.ninjavan.co/th-th/tracking?id=${encodeURIComponent(code)}`,
  spx: (code) => `https://spx.co.th/track?${new URLSearchParams({ tracking_number: code }).toString()}`,
  other: null,
}

export const trackingUrl = (carrier: ShippingCarrier, code: string): string | null =>
  TRACKING_URL[carrier]?.(code.trim()) ?? null
